// Primary CTA family. Three variants per the prototype:
//   - primary → rame fill, paper-tone label, drop-shadow + inset highlight
//   - ghost   → transparent fill, ink label, inset 1px paper-muted ring
//   - quiet   → transparent fill, ink-muted label, smaller type, no chrome
//
// Pixel sizes preserved verbatim: 52 px height, 22 px horizontal padding,
// fully rounded (rounded-full). Press state (mousedown/up) shrinks to 0.985
// to give the same tactile feedback as ICSButton in shared.jsx.
//
// Ported from handoff_input/design/screens/shared.jsx :: ICSButton.

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonStyles = cva(
  // base
  [
    "inline-flex items-center justify-center gap-2",
    "h-13 rounded-full",
    "font-sans font-medium leading-none tracking-[-0.1px]",
    "transition-[transform,background-color,box-shadow] duration-150",
    "active:scale-[0.985]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "outline-none focus-visible:ring-2 focus-visible:ring-rame focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-rame text-[#FDFBF7] px-[22px] text-base",
          "shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_6px_18px_rgba(184,98,58,0.22)]",
          "hover:bg-rame-hover",
        ].join(" "),
        ghost: [
          "bg-transparent text-ink px-[22px] text-base",
          "shadow-[inset_0_0_0_1px_theme(colors.paper-muted)]",
          "hover:bg-paper-muted/40",
        ].join(" "),
        quiet: [
          "bg-transparent text-ink-muted px-[22px] text-sm",
          "hover:text-ink",
        ].join(" "),
      },
      full: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      full: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ variant, full, className, type, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        className={cn(buttonStyles({ variant, full }), className)}
        {...rest}
      />
    );
  },
);
